// external
import classNames from "classnames";
import { useContext, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// components
import CustomButton from "./partials/custom-button/custom-button";

// state management
import useAidResultsBuild from "pages/fao-builder/hooks/use-aid-results-build";
import { FaoBuilderContext } from "context/FaoBuilderContext";

// assets
import Icons from "../assets/icons";

// helpers
import {
  formatModelValues,
  BUILD_BTN_VARIANTS,
  renderTooltip,
  handleUnpublishModel,
} from "pages/fao-builder/helpers/published-model-helpers";

// constants
import MODEL_BUILD_STATUS from "pages/fao-builder/constants/model-build-status";

// constants to list the model details to show in the card
const MODEL_DETAILS = ["Author", "Last updated"];
const SUMMARY_DETAILS = [
  "Est. Enrollment",
  "Est. NTR",
  "Est. Inst. Aid",
  "Est. Discount Rate",
];

function PublishedModel({ model, setModels, models }) {
  const navigate = useNavigate();
  const { handleAidResultsBuild } = useAidResultsBuild(
    model,
    setModels,
    models
  );

  const { aidResultsBuildStatus, setModelConfig } =
    useContext(FaoBuilderContext);

  const handleBuildBtnClick = useCallback(async () => {
    try {
      // build the aid results
      await handleAidResultsBuild();
    } catch (error) {
      console.error(error);
    }
  }, [handleAidResultsBuild]);

  const handleUnpublishModelAction = useCallback(() => {
    handleUnpublishModel(model, navigate);
  }, [model, navigate]);

  const modelBuildStatus = useMemo(() => {
    return aidResultsBuildStatus[model.id]?.status === "building"
      ? MODEL_BUILD_STATUS.BUILDING
      : model.aidResults?.generated
      ? MODEL_BUILD_STATUS.GENERATED
      : MODEL_BUILD_STATUS.NOT_GENERATED;
  }, [aidResultsBuildStatus, model.id, model.aidResults]);

  const modelBuildBtn = useMemo(() => {
    const btnDetails = BUILD_BTN_VARIANTS[modelBuildStatus];
    if (modelBuildStatus === MODEL_BUILD_STATUS.GENERATED) {
      return null;
    }
    return (
      <CustomButton
        variant={btnDetails.variant}
        iconBefore={btnDetails.icon}
        onClick={handleBuildBtnClick}
        disabled={modelBuildStatus === MODEL_BUILD_STATUS.BUILDING}
      >
        {btnDetails.btnLabel}
      </CustomButton>
    );
  }, [modelBuildStatus, handleBuildBtnClick]);

  const buildError = useMemo(() => {
    return aidResultsBuildStatus[model.id]?.error;
  }, [aidResultsBuildStatus, model.id]);

  const renderBuildBtn = useCallback(() => {
    return (
      <div className="flex items-end justify-between">
        <div className="flex flex-col items-start gap-2">
          {buildError && <p className="text-sm text-red-500">{buildError}</p>}
          <div className="flex gap-2">
            <CustomButton
              variant="secondary"
              iconBefore={
                <Icons.Edit aria-label="Unpublish" customClass="h-full w-4" />
              }
              onClick={handleUnpublishModelAction}
              disabled={modelBuildStatus === MODEL_BUILD_STATUS.BUILDING}
            >
              Unpublish model
            </CustomButton>
            {modelBuildBtn}
          </div>
        </div>
        <div className="flex items-end gap-2">
          {MODEL_DETAILS.map((item, index) => {
            const formattedValues = formatModelValues(item, model);
            return (
              <div
                className="flex justify-between gap-2 text-left font-normal"
                key={`model-details-${index}`}
              >
                <p className="text-gray-400">{item}</p>
                <p
                  className={classNames({
                    [formattedValues.styling]: true,
                  })}
                >
                  {formattedValues.content}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [
    modelBuildStatus,
    modelBuildBtn,
    buildError,
    handleUnpublishModelAction,
    model,
  ]);

  const renderModelCard = () => {
    const formattedValues = formatModelValues("Aid results", model);
    return (
      <div className="flex h-full w-full flex-col rounded-md p-4">
        <div className="">
          <div className="flex items-end justify-between">
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold text-blue-03">
                {`${model.versionName ? model.versionName : "..."}`}
              </h3>
              <p className="text-sm font-light text-gray-500">{model.id}</p>
            </div>
            <p
              className={classNames({
                [formattedValues.styling]: true,
              })}
            >
              {formattedValues.content}
            </p>
          </div>
          <div className="mt-5 grid w-full grid-cols-2 justify-between">
            <div className="my-8 mt-0 flex flex-col gap-1">
              {SUMMARY_DETAILS.map((item, index) => {
                const formattedValues = formatModelValues(item, model);
                return (
                  <div
                    className="grid grid-cols-2 gap-2 font-normal"
                    key={`summary-details-${index}`}
                  >
                    <p className="justify-self-start text-gray-400">{item}</p>
                    <p
                      className={classNames("justify-self-start", {
                        [formattedValues.styling]: true,
                      })}
                    >
                      {formattedValues.content}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="flex w-1/2 flex-col text-left font-normal">
              <p className="col-start-1 m-0 text-gray-400">Description</p>
              <p className="col-start-1 ">
                {model.description || "No description"}
              </p>
            </div>
          </div>
        </div>
        <div>{renderBuildBtn()}</div>
      </div>
    );
  };
  const handleViewAidResults = () => {
    setModelConfig(model);
    navigate("../fao-builder/fao-view-results");
  };

  const clickableId = useMemo(() => {
    return modelBuildStatus === MODEL_BUILD_STATUS.GENERATED ? "1" : "2";
  }, [modelBuildStatus]);

  const tooltip = useMemo(() => {
    return renderTooltip(modelBuildStatus === MODEL_BUILD_STATUS.GENERATED);
  }, [modelBuildStatus]);

  return (
    <div>
      <a id={`clickable${clickableId}`}>
        {modelBuildStatus === MODEL_BUILD_STATUS.GENERATED ? (
          <CustomButton
            customClass="h-full w-full !p-0 hover:border-blue-03 border border-gray-300 transition-all"
            key={model.id}
            aria-label={`Navigate to ${model.versionName} FAO model`}
            onClick={handleViewAidResults}
          >
            {renderModelCard()}
          </CustomButton>
        ) : (
          <div className="h-full w-full rounded-md border border-gray-300 !p-0 transition-all hover:border-blue-03">
            {renderModelCard()}
          </div>
        )}
      </a>
      {tooltip}
    </div>
  );
}

export default PublishedModel;
